import {hasMegaPayBusinessCapabilities} from '../../../../_metronic/helpers/brand'
import type {StuffIdFilters} from './stuffidCatalog'

// The public goods/services ID search (جستجوی شناسه کالا/خدمت). For ASA's brands it lives on its
// own host; stuffid.mega-pay.ir is its old address and moves browsers there (public/index.html).
// Other brands keep the page inside their own panel.
export const STUFFID_SITE_URL = 'https://stuffid.asatsp.ir'
export const STUFFID_PANEL_PATH = '/apps/stuffid-management/stuffids'
// The search term a link may carry, e.g. https://stuffid.asatsp.ir/?q=کابل
export const STUFFID_QUERY_PARAM = 'q'
// The group and subgroup a link may open, e.g. https://stuffid.asatsp.ir/?g=12&t=34
export const STUFFID_GROUP_PARAM = 'g'
export const STUFFID_SUBGROUP_PARAM = 't'
// The filters a link may set: kind=goods|services, scope=all|dedicated (general IDs are the
// page's default and need none), vat=taxable|exempt.
export const DEFAULT_STUFFID_FILTERS: StuffIdFilters = {kind: '', scope: '1', vat: ''}

const STUFFID_HOSTNAMES = ['stuffid.asatsp.ir', 'stuffid.mega-pay.ir']

export const isStuffIdHostname = (hostname: string) =>
  STUFFID_HOSTNAMES.includes(
    String(hostname || '')
      .trim()
      .toLowerCase()
  )

export const getStuffIdSearchUrl = (query = '', asaBrand = hasMegaPayBusinessCapabilities()) => {
  const base = asaBrand ? `${STUFFID_SITE_URL}/` : STUFFID_PANEL_PATH
  const term = String(query || '').trim()
  return term ? `${base}?${STUFFID_QUERY_PARAM}=${encodeURIComponent(term)}` : base
}

export const readStuffIdQuery = (search: string) =>
  (new URLSearchParams(search).get(STUFFID_QUERY_PARAM) || '').trim()

const readPositiveId = (params: URLSearchParams, name: string) => {
  const value = Number(params.get(name))
  return Number.isInteger(value) && value > 0 ? value : undefined
}

export const readStuffIdCategory = (search: string) => {
  const params = new URLSearchParams(search)
  return {
    groupId: readPositiveId(params, STUFFID_GROUP_PARAM),
    tagId: readPositiveId(params, STUFFID_SUBGROUP_PARAM),
  }
}

export const readStuffIdFilters = (search: string): StuffIdFilters => {
  const params = new URLSearchParams(search)
  const read = (name: string) => (params.get(name) || '').trim().toLowerCase()
  const kind = read('kind')
  const scope = read('scope')
  const vat = read('vat')
  return {
    kind: kind === 'goods' ? '1' : kind === 'services' ? '2' : '',
    scope: scope === 'all' ? '' : scope === 'dedicated' ? '2' : DEFAULT_STUFFID_FILTERS.scope,
    vat: vat === 'taxable' ? '1' : vat === 'exempt' ? '2' : '',
  }
}

/** The address query of a search: its term, group, subgroup and filters; other parameters are kept. */
export const withStuffIdSearchParams = (
  search: string,
  term: string,
  category: {groupId?: number; tagId?: number},
  filters: StuffIdFilters = DEFAULT_STUFFID_FILTERS
) => {
  const params = new URLSearchParams(search)
  const put = (name: string, value?: string | number) =>
    value ? params.set(name, String(value)) : params.delete(name)
  put(STUFFID_QUERY_PARAM, String(term || '').trim())
  put(STUFFID_GROUP_PARAM, category.groupId)
  put(STUFFID_SUBGROUP_PARAM, category.tagId)
  put('kind', filters.kind === '1' ? 'goods' : filters.kind === '2' ? 'services' : '')
  put('scope', filters.scope === '' ? 'all' : filters.scope === '2' ? 'dedicated' : '')
  put('vat', filters.vat === '1' ? 'taxable' : filters.vat === '2' ? 'exempt' : '')
  const query = params.toString()
  return query ? `?${query}` : ''
}
